import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useOnboardingStore } from '../../store/onboardingStore';
import { ONBOARDING_STEPS, TOTAL_STEPS } from './onboardingSteps';
import { onboardingService } from '../../services/onboardingService';
import { useToast } from '../common/Toast';
import { useLanguage } from '../../features/language/useLanguage';
import { OnboardingCompletionModal } from './OnboardingCompletionModal';
import { OnboardingSpotlight } from './OnboardingSpotlight';
import { OnboardingInventoryFieldGuide } from './OnboardingInventoryFieldGuide';
import { OnboardingFieldGuide } from './OnboardingFieldGuide';
import {
  type FieldGuideConfig,
  purchaseOrderFieldGuideConfig,
  workOrderFieldGuideConfig,
  cuppingFieldGuideConfig,
  cuppingScoreFieldGuideConfig,
  fgOpenFieldGuideConfig,
  fgCreateFieldGuideConfig,
  fgCompleteFieldGuideConfig,
  fgCompletePanelFieldGuideConfig,
  fgViewFieldGuideConfig,
  salesOrderOpenFieldGuideConfig,
  salesOrderCreateFieldGuideConfig,
  salesOrderStatusModalFieldGuideConfig,
  salesOrderStatusRowFieldGuideConfig,
} from './onboardingFieldConfigs';
import { OnboardingCoachCard } from './OnboardingCoachCard';
import { OnboardingWelcomeCard } from './OnboardingWelcomeCard';
import { isElementVisible } from './onboardingDom';

// ---------------------------------------------------------------------------
// Practice-step guide registry (populated per-step in later tasks).
// Keeping these module-level means the overlay re-renders don't recreate them.
// ---------------------------------------------------------------------------

/** Maps practice step number → FieldGuideConfig. Steps present here use the
 *  guided user-submitted flow; absent steps fall back to auto-create via
 *  runPracticeAndAdvance. */
const PRACTICE_GUIDE_CONFIGS: Record<number, FieldGuideConfig | FieldGuideConfig[]> = {
  2: purchaseOrderFieldGuideConfig,
  3: workOrderFieldGuideConfig,
  // Step 4: create the session (modal), then score it (detail page).
  4: [cuppingFieldGuideConfig, cuppingScoreFieldGuideConfig],
  // Step 5: switch to the Packing tab + open the form (route), create the
  // production draft (modal → Save closes it), click "Complete" on the new draft
  // row in the panel, then complete it (modal). Listed most-specific first:
  // create modal → complete modal → panel-complete button → route fallback, so
  // whichever phase's element is on screen wins (the route phase is the default
  // only when no draft and no modal exist).
  // fgView sits above fgCompletePanel for the same reason fgComplete does: the
  // panel's per-row buttons stay laid out behind an open modal, so the
  // more-specific modal phase has to win.
  5: [
    fgCreateFieldGuideConfig,
    fgCompleteFieldGuideConfig,
    fgViewFieldGuideConfig,
    fgCompletePanelFieldGuideConfig,
    fgOpenFieldGuideConfig,
  ],
  // Step 6: open the create form (route), create the sales order (modal → Save
  // closes it), then walk the new order Draft → Sent → Delivered. Listed
  // most-specific first so the right phase wins for the current DOM state:
  //   create modal → mark-as edit modal → row status button → open-form route.
  // The open-form route fallback is active only when no order exists and no
  // modal is open.
  6: [
    salesOrderCreateFieldGuideConfig,
    salesOrderStatusModalFieldGuideConfig,
    salesOrderStatusRowFieldGuideConfig,
    salesOrderOpenFieldGuideConfig,
  ],
};

/** Maps practice step number → the custom DOM event that signals the user
 *  completed the real create action. Listeners are registered in a useEffect. */
const PRACTICE_CREATED_EVENT: Record<number, string> = {
  2: 'qrema:purchase-order-created',
  3: 'qrema:work-order-completed',
  4: 'qrema:cupping-created',
  5: 'qrema:finished-good-produced',
  6: 'qrema:sales-order-delivered',
};

function errMsg(e: unknown): string {
  const ax = e as { response?: { data?: { error?: string } }; message?: string };
  return ax?.response?.data?.error || ax?.message || 'Something went wrong. Please try again.';
}

// Reactively report whether an element matching `selector` is laid out and
// visible. Used to detect the inventory create modal opening and whether a
// collapsed sidebar group has expanded to reveal its child link.
function useElementPresence(selector: string | null): boolean {
  const [present, setPresent] = useState(false);
  useEffect(() => {
    if (!selector) {
      // Defer the reset out of the effect body to avoid a synchronous
      // setState-in-effect; one frame late is imperceptible here.
      const reset = requestAnimationFrame(() => setPresent(false));
      return () => cancelAnimationFrame(reset);
    }
    let raf = 0;
    const tick = () => {
      // isElementVisible (not just a box-size test) so a collapsed sidebar
      // group's still-mounted child link doesn't read as present.
      const v = isElementVisible(document.querySelector<HTMLElement>(selector));
      setPresent((prev) => (prev === v ? prev : v));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [selector]);
  return present;
}

// Like useElementPresence but for several selectors at once: returns a signature
// string that changes whenever the SET of present selectors changes. A single
// "any present" boolean is not enough when phases overlap — e.g. the panel's
// per-row Complete button stays present while the complete modal opens over it,
// so a boolean never flips and the overlay would not recompute the active phase.
function useElementPresenceSignature(selectors: string[]): string {
  const key = selectors.join('|');
  const [sig, setSig] = useState('');
  useEffect(() => {
    if (!key) {
      // Defer the reset out of the effect body to avoid a synchronous
      // setState-in-effect; one frame late is imperceptible here.
      const reset = requestAnimationFrame(() => setSig(''));
      return () => cancelAnimationFrame(reset);
    }
    const list = key.split('|');
    let raf = 0;
    const tick = () => {
      const next = list
        .map((sel) => (isElementVisible(document.querySelector<HTMLElement>(sel)) ? '1' : '0'))
        .join('');
      setSig((prev) => (prev === next ? prev : next));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [key]);
  return sig;
}

export const OnboardingOverlay: React.FC = () => {
  const state = useOnboardingStore((s) => s.state);
  const advance = useOnboardingStore((s) => s.advance);
  const skip = useOnboardingStore((s) => s.skip);
  const welcomeSeen = useOnboardingStore((s) => s.welcomeSeen);
  const setWelcomeSeen = useOnboardingStore((s) => s.setWelcomeSeen);
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);
  const location = useLocation();
  const { showToast } = useToast();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  // Tracks which step has already had its seed call dispatched this mount cycle
  // so we don't double-seed if the component re-renders while seed() is in-flight.
  // Keyed by TOUR RUN + step, not step alone. Replay wipes every sample row and
  // starts over, so a run-blind guard refused to re-seed any step this page
  // session had already seeded — the user replayed and found the packing and
  // cupping pickers empty, because cleanup had deleted what the guard now
  // believed was still there. startedAt is stamped fresh by /start and /replay,
  // which makes it the identity of the run.
  const seededForStep = useRef<string | null>(null);

  // Selectors for the presence hooks — computed every render (hooks stay
  // unconditional); guarded so they are null when not relevant.
  const def = state ? ONBOARDING_STEPS.find((s) => s.step === state.currentStep) : undefined;
  const route = def?.route ?? '';
  const onRightScreen =
    !!def && (location.pathname === route || location.pathname.startsWith(route + '/'));
  const navIsGroup = !!def && !('item' in def.nav);
  const navChildSel =
    def && navIsGroup && !onRightScreen && 'child' in def.nav ? def.nav.child : null;

  const inventoryModalOpen = useElementPresence(
    state?.currentStep === 1 ? '[data-test-id="inventory-create-name"]' : null,
  );
  const navChildPresent = useElementPresence(navChildSel);

  // Guide-form detection for practice steps. A step may have more than one guide
  // config (e.g. step 4: the create modal, then the scoring page) — normalise to
  // an array and pick the one whose `openWhen` currently matches.
  const stepGuideEntry = state ? PRACTICE_GUIDE_CONFIGS[state.currentStep] : undefined;
  const practiceGuideConfigs: FieldGuideConfig[] = stepGuideEntry
    ? Array.isArray(stepGuideEntry)
      ? stepGuideEntry
      : [stepGuideEntry]
    : [];
  // A step may register several modal-based phases (e.g. step 5: create modal →
  // complete modal). Watch a combined selector so the overlay re-renders when any
  // appears/disappears; the precise per-config match happens in activeGuideConfig.
  const practiceGuideSelectors = practiceGuideConfigs.flatMap((c) => {
    if (!c.openWhen) return [];
    if ('selectorPresent' in c.openWhen) return [c.openWhen.selectorPresent];
    if ('anyPresent' in c.openWhen) return c.openWhen.anyPresent;
    return [];
  });
  const guidePresenceSignature = useElementPresenceSignature(practiceGuideSelectors);

  // Step 1 finishes when the user creates their first real item. The create
  // modal fires `qrema:inventory-item-created` on a successful create (only —
  // not on edit/cancel/backdrop), so this is a reliable advance signal even
  // though the review phase leaves the modal closable. Without it the overlay
  // falls back to the step-1 coach card and looks like it reset.
  useEffect(() => {
    const onCreated = () => {
      const s = useOnboardingStore.getState().state;
      if (s?.status === 'in_progress' && s.currentStep === 1) {
        void advance().catch((e) => showToast(errMsg(e), 'error'));
      }
    };
    window.addEventListener('qrema:inventory-item-created', onCreated);
    return () => window.removeEventListener('qrema:inventory-item-created', onCreated);
  }, [advance, showToast]);

  // Practice steps (2–5): listen for the "real create" custom events. When one
  // fires and we're on the matching step, capture any ids from e.detail into
  // sampleRefs and advance. completedSteps short-circuit is handled inside
  // advance() itself. Step 2 fires 'qrema:purchase-order-created' today; steps
  // 3–5 will fire their events once their guide configs are added.
  useEffect(() => {
    const handlers: Array<[string, EventListener]> = Object.entries(PRACTICE_CREATED_EVENT).map(
      ([stepStr, eventName]) => {
        const step = Number(stepStr);
        const handler: EventListener = (evt) => {
          const s = useOnboardingStore.getState().state;
          if (s?.status !== 'in_progress' || s.currentStep !== step) return;
          // Merge any ids passed via e.detail into sampleRefs.
          const detail = (evt as CustomEvent<Record<string, string>>).detail ?? {};
          const sampleRefs: Record<string, string> = { ...(s.sampleRefs ?? {}), ...detail };
          void onboardingService
            .updateState({ ...s, sampleRefs })
            .then(() => advance())
            .catch((e) => showToast(errMsg(e), 'error'));
        };
        return [eventName, handler];
      },
    );
    for (const [name, handler] of handlers) {
      window.addEventListener(name, handler);
    }
    return () => {
      for (const [name, handler] of handlers) {
        window.removeEventListener(name, handler);
      }
    };
  }, [advance, showToast]);

  // Seed-on-entry: when the user arrives on a practice step that has a guide
  // config and the sample supplier/entities aren't seeded yet, call seed() once
  // and store the returned ids. The seededForStep ref prevents a double-call if
  // this effect re-runs while the async seed() is in-flight. Active for any step
  // present in PRACTICE_GUIDE_CONFIGS (step 2 today).
  useEffect(() => {
    if (!state || state.status !== 'in_progress') return;
    const step = state.currentStep;
    if (!(step in PRACTICE_GUIDE_CONFIGS)) return;
    if (!onRightScreen) return;
    // Re-seed when the roasted bulks are missing too: tours seeded before the
    // sample roasted bulks existed have supplierId but no roastedBulkId1.
    // seed() is idempotent server-side, so it just backfills the missing refs.
    // finishedGoodId is part of the check so tours seeded before the sample
    // finished good existed get it backfilled instead of reaching step 6 with an
    // empty product picker.
    if (state.sampleRefs?.supplierId && state.sampleRefs?.roastedBulkId1 && state.sampleRefs?.finishedGoodId)
      return; // already seeded
    const seedKey = `${state.startedAt ?? 'unknown'}:${step}`;
    if (seededForStep.current === seedKey) return; // already in-flight for this run's step
    seededForStep.current = seedKey;
    void (async () => {
      try {
        const seeded = await onboardingService.seed();
        // seed() is a round trip, and the user can end the tour during it. `state`
        // is the value captured when this effect ran, so writing it back now would
        // revive a tour that was just skipped — and re-attach refs to sample rows
        // the skip's cleanup deleted. Re-read the live state and drop the result
        // if the tour is over. (The server refuses to seed once it is, which
        // closes the other half of the same race.)
        const live = useOnboardingStore.getState().state;
        if (!live || live.status !== 'in_progress') return;
        const sampleRefs: Record<string, string> = {
          ...(live.sampleRefs ?? {}),
          supplierId: seeded.supplierId,
          greenItemId: seeded.greenItemId,
          customerId: seeded.customerId,
          cupperId: seeded.cupperId,
          machineTypeId: seeded.machineTypeId,
          ...(seeded.roastedBulkId1 ? { roastedBulkId1: seeded.roastedBulkId1 } : {}),
          ...(seeded.roastedBulkId2 ? { roastedBulkId2: seeded.roastedBulkId2 } : {}),
          ...(seeded.finishedGoodId ? { finishedGoodId: seeded.finishedGoodId } : {}),
        };
        await onboardingService.updateState({ ...live, sampleRefs });
        useOnboardingStore.getState().setState({ ...live, sampleRefs });
        // Refresh sample-bearing pickers that may have fetched before seeding
        // completed (e.g. the cupping modal mounts with the list page, so its
        // roasted-bulk/cupper queries can resolve empty before the seed lands).
        queryClient.invalidateQueries({ queryKey: ['roasted-bulk-groups'] });
        queryClient.invalidateQueries({ queryKey: ['roasters'] });
      } catch (e) {
        showToast(errMsg(e), 'error');
        // Reset guard so the next render can retry.
        seededForStep.current = null;
      }
    })();
  }, [state, onRightScreen, showToast, queryClient]);

  // Safety net for tour teardown. The spotlight renders its scrim/coach via a
  // portal into document.body, and the guided steps drive Headless UI Dialogs
  // that mark #root `inert`/`aria-hidden`. When the tour ends (skip/complete) the
  // overlay unmounts, but a body-level scrim node can be orphaned (React's removal
  // races Headless UI reparenting body children) and #root can be left inert —
  // leaving an invisible click-blocker over the app (e.g. the header "Replay tour"
  // button) that previously only a full reload could clear. When onboarding is no
  // longer in progress, sweep any leftover scrim/coach nodes and clear a stale
  // inert #root (unless a real dialog is still open). This effect still runs even
  // when the render below returns null, because hooks run before the early return.
  useEffect(() => {
    if (state?.status === 'in_progress') return;
    document.querySelectorAll('.onboarding-scrim, .onboarding-coach').forEach((el) => el.remove());
    const root = document.getElementById('root');
    if (root && !document.querySelector('[role="dialog"]')) {
      root.removeAttribute('inert');
      root.removeAttribute('aria-hidden');
    }
  }, [state?.status]);

  if (!state) return null;
  const showPanel = state.status === 'in_progress' && !!def && !completing;
  if (!showPanel && !completing) return null;

  // --- step actions ---------------------------------------------------------

  // Practice steps (2-5): one click sets up the tagged sample chain, then
  // advances. Real forms aren't filled by hand — that would create untagged
  // records the end-of-tour cleanup could never remove.
  const runPracticeAndAdvance = async () => {
    if (!state || !def) return;
    // Already completed this practice step (e.g. the user stepped back to an
    // earlier step and is now clicking forward through steps they finished
    // before). Don't recreate the sample record — just advance. A genuine redo
    // goes through "Replay tour", which clears the old chain first.
    if (state.completedSteps?.includes(state.currentStep)) {
      await safeAdvance();
      return;
    }
    setBusy(true);
    try {
      const refs: Record<string, string> = { ...(state.sampleRefs || {}) };
      const step = state.currentStep;
      // Re-seed when machineTypeId is missing too: users who seeded before the
      // sample machine was introduced have refs.supplierId but no machineTypeId.
      // The practice roasts are checked for the same reason — state seeded before
      // cupping became a two-roast comparison carries neither id, and step 4's
      // fallback cannot build a session without both.
      // seed() is idempotent server-side, so it just backfills the missing refs.
      if (
        step >= 2 &&
        step <= 6 &&
        (!refs.supplierId ||
          !refs.machineTypeId ||
          !refs.roastedBulkId1 ||
          !refs.roastedBulkId2 ||
          !refs.finishedGoodId)
      ) {
        const seeded = await onboardingService.seed();
        refs.supplierId = seeded.supplierId;
        refs.greenItemId = seeded.greenItemId;
        refs.customerId = seeded.customerId;
        refs.cupperId = seeded.cupperId;
        refs.machineTypeId = seeded.machineTypeId;
        if (seeded.roastedBulkId1) refs.roastedBulkId1 = seeded.roastedBulkId1;
        if (seeded.roastedBulkId2) refs.roastedBulkId2 = seeded.roastedBulkId2;
        if (seeded.finishedGoodId) refs.finishedGoodId = seeded.finishedGoodId;
      }
      // Steps 2–6 all have guide configs, so the branches below are only reached
      // as a fallback (e.g. user clicks Next before the form detects their action).
      if (step === 2) {
        const r = await onboardingService.createSamplePurchaseOrder(refs.supplierId, refs.greenItemId);
        refs.poId = r.poId;
        refs.greenLotId = r.greenLotId;
      } else if (step === 3) {
        const r = await onboardingService.createSampleWorkOrder(refs.greenItemId, refs.greenLotId, refs.machineTypeId);
        refs.woId = r.woId;
        refs.woNumber = r.woNumber;
        refs.roastedItemId = r.roastedItemId;
      } else if (step === 4) {
        const r = await onboardingService.createSampleCupping(
          [refs.roastedBulkId1, refs.roastedBulkId2],
          refs.cupperId,
        );
        refs.cuppingId = r.cuppingId;
      }
      // Steps 5 (packing) and 6 (selling) have no auto-create fallback and need
      // none: the seed stocks a sample finished good, so the step-6 sale always
      // has something to sell whether or not the user packed one.
      await onboardingService.updateState({ ...state, sampleRefs: refs });
      await advance();
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  // Skipping runs the server-side teardown of every tagged practice record, and
  // that can fail (an FK the cleanup does not clear yet). The server then refuses
  // to mark the tour skipped so the caller can retry — but `void skip()` threw
  // the rejection away, so the button did nothing at all and the user was left
  // believing the tour had ended with its sample data quietly cleaned up. Show
  // what happened instead.
  const safeSkip = async () => {
    setBusy(true);
    try {
      await skip();
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const safeAdvance = async () => {
    setBusy(true);
    try {
      await advance();
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const goPrev = async () => {
    if (!state) return;
    if (state.currentStep <= 1) {
      setWelcomeSeen(false); // back to the welcome bridge
      return;
    }
    setBusy(true);
    try {
      const updated = await onboardingService.updateState({
        ...state,
        currentStep: state.currentStep - 1,
      });
      useOnboardingStore.getState().setState(updated);
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const onNext = () => {
    if (!def) return;
    if (def.kind === 'practice') {
      void runPracticeAndAdvance();
      return;
    }
    if (def.kind === 'qora') {
      setCompleting(true); // open the completion modal
      return;
    }
    void safeAdvance(); // inventory, report
  };

  // --- presentation ---------------------------------------------------------

  // Stepping BACK into a practice step the user already finished must not ask
  // them to do it again: the sample chain is single-use (the packed finished
  // good is already sold by step 6, so a second sales order would find no
  // stock). Show the plain coach card with Next enabled instead — Next
  // short-circuits through runPracticeAndAdvance's completedSteps branch.
  const alreadyCompleted = !!state.completedSteps?.includes(state.currentStep);
  // A user who has been through the tour before is not being taught the flow
  // any more — they are revisiting it. Gating Next behind each practice step
  // would make them re-run work they have already done just to reach the step
  // they actually came back for, so Next stays live on every step for them.
  // First-timers keep the gate: doing the practice IS the tour.
  const canSkipAhead = !!state.hasOnboardedBefore;

  const showWelcome = state.currentStep === 1 && !welcomeSeen;
  const showFieldGuide =
    state.currentStep === 1 && !showWelcome && onRightScreen && inventoryModalOpen;
  const navPhase = !onRightScreen;

  // Guided-practice detection: pick the config (if any) whose form is currently
  // open. A step can register several (e.g. step 4: create modal → scoring page).
  const hasGuideConfig = practiceGuideConfigs.length > 0;
  const activeGuideConfig = practiceGuideConfigs.find((c) => {
    if (!c.openWhen) return false;
    if ('route' in c.openWhen) return location.pathname.startsWith(c.openWhen.route);
    // selectorPresent / anyPresent: precise per-config visibility; guidePresenceSignature
    // drives the re-render cadence so this re-evaluates when any phase element appears or
    // disappears (including a modal opening over an already-present panel button).
    void guidePresenceSignature;
    if ('anyPresent' in c.openWhen) {
      return c.openWhen.anyPresent.some((sel) =>
        isElementVisible(document.querySelector<HTMLElement>(sel)),
      );
    }
    return isElementVisible(document.querySelector<HTMLElement>(c.openWhen.selectorPresent));
  });
  // OnboardingFieldGuide holds the current field index in local state. Several
  // steps register multiple phases (e.g. step 5: packing-tab phase → create
  // modal → complete modal); when the active phase changes the guide must
  // remount so its index restarts at the first field. Keying it by the active
  // config's openWhen identity forces that remount — without it the new phase
  // inherits the previous phase's index and skips straight to its review.
  const activeGuideKey = activeGuideConfig?.openWhen
    ? 'route' in activeGuideConfig.openWhen
      ? `route:${activeGuideConfig.openWhen.route}`
      : 'anyPresent' in activeGuideConfig.openWhen
        ? `any:${activeGuideConfig.openWhen.anyPresent.join(',')}`
        : `sel:${activeGuideConfig.openWhen.selectorPresent}`
    : undefined;
  // Engage the guided branch only on the step's own screen — this guards the
  // modal-presence case, where a selector could theoretically match on another
  // page. (For route-based forms onRightScreen is implied by the route match.)
  const showPracticeGuide =
    !showWelcome &&
    !showFieldGuide &&
    !!activeGuideConfig &&
    def?.kind === 'practice' &&
    onRightScreen &&
    // Already finished: the landing-page phase steps aside so the coach card can
    // offer Next. Any phase with a form actually open still runs, so choosing to
    // redo the step is guided exactly as it was the first time.
    !(alreadyCompleted && activeGuideConfig.entryPhase);

  let navTargetSel: string | undefined;
  if (navPhase && def) {
    if ('item' in def.nav) navTargetSel = def.nav.item;
    else navTargetSel = navChildPresent ? def.nav.child : def.nav.groupToggle;
  }
  const spotlightTarget = navPhase ? navTargetSel : def?.actionTarget;
  // The report and Qora steps are "look at this" moments — keep the live screen
  // readable instead of dimming it.
  const keepScreenVisible = !navPhase && (def?.kind === 'report' || def?.kind === 'qora');

  return (
    <>
      {showPanel && showWelcome && (
        <OnboardingSpotlight placement="top-right">
          {() => (
            <OnboardingWelcomeCard
              onStart={() => setWelcomeSeen(true)}
              onSkip={() => void safeSkip()}
            />
          )}
        </OnboardingSpotlight>
      )}

      {showPanel && !showWelcome && showFieldGuide && (
        <OnboardingInventoryFieldGuide onSkip={() => void safeSkip()} />
      )}

      {/* Guided practice: the step's current form is open — show the field guide. */}
      {showPanel && showPracticeGuide && activeGuideConfig && (
        <OnboardingFieldGuide
          key={activeGuideKey}
          config={activeGuideConfig}
          onSkip={() => void safeSkip()}
          onPrevStep={() => void goPrev()}
        />
      )}

      {showPanel && !showWelcome && !showFieldGuide && !showPracticeGuide && def && (
        <OnboardingSpotlight
          targetSelector={
            // Guided practice step whose form is NOT open yet (e.g. step 2 on
            // the PO list page): spotlight the step's own create-button
            // actionTarget so the user knows what to click. Opening the form
            // then trips practiceFormOpen and the field guide takes over.
            // A step the user already finished keeps its highlight: finishing it
            // once does not take the option away, it only adds a second one.
            // Redo it or press Next — the card offers both.
            !navPhase && hasGuideConfig && def.kind === 'practice'
              ? def.actionTarget
              : spotlightTarget
          }
          dim={!keepScreenVisible}
        >
          {({ mode }) => (
            <OnboardingCoachCard
              stepNumber={state.currentStep}
              totalSteps={TOTAL_STEPS}
              module={t(`onboarding.steps.${def.step}.module`, undefined, def.module)}
              body={
                navPhase
                  ? t(`onboarding.steps.${def.step}.nav`, undefined, def.navInstruction)
                  : t(`onboarding.steps.${def.step}.instruction`, undefined, def.instruction)
              }
              // The one place the completed state shows: the instruction above still
              // stands (redoing is allowed and the practice data is re-stocked for
              // it), so this says the step is already done AND that Next is open.
              hint={
                alreadyCompleted
                  ? t(
                      'onboarding.coach.alreadyDoneHint',
                      undefined,
                      "You've already done this — redo it if you like, or click Next to carry on.",
                    )
                  : navPhase
                    ? t('onboarding.coach.navHint', undefined, 'Click the highlighted item in the menu →')
                    : mode === 'guide'
                      ? t('onboarding.coach.guideHint', undefined, 'Use the highlighted control →')
                      : def.hint
                        ? t(`onboarding.steps.${def.step}.hint`, undefined, def.hint)
                        : undefined
              }
              nextLabel={!navPhase && def.kind === 'qora'
                ? t('onboarding.coach.finishTour', undefined, 'Finish tour')
                : t('onboarding.coach.next', undefined, 'Next')}
              // Guided practice steps (form not yet open) don't offer an auto-
              // create Next — the user must open the real form. Auto-create Next
              // is preserved for steps without a guide config (steps 3–5 today).
              // A completed step is done wherever the user happens to be standing:
              // sending them back to its screen just to press Next is busywork.
              // This is the ONLY thing being finished changes — the step's own
              // guide, highlight and instruction all stay available for a redo.
              nextDisabled={
                !alreadyCompleted &&
                !canSkipAhead &&
                (navPhase || (hasGuideConfig && def.kind === 'practice' && !navPhase))
              }
              onNext={onNext}
              onPrev={() => void goPrev()}
              onSkip={() => void safeSkip()}
              busy={busy}
            />
          )}
        </OnboardingSpotlight>
      )}

      <OnboardingCompletionModal isOpen={completing} onClose={() => setCompleting(false)} />
    </>
  );
};
